import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { Plus, ChevronLeft } from 'lucide-react';

import huntTripSvg from '@/assets/icons/ui/hunt-trip.svg?raw';
import noHuntsPng from '@/assets/illustrations/empty/no-hunts.png';

import { useHuntStore } from '@/stores/hunt-store';
import { useSeasonStore } from '@/stores/season-store';
import { useUserProfileStore } from '@/stores/user-profile-store';
import { UpsellSheet } from '@/components/common/UpsellSheet';
import { UPSELL_COPY } from '@/constants/upsell-copy';
import { OperationalGuide } from '@/components/common/OperationalGuide';
import type { Hunt } from '@/types';

function HuntCard({ hunt, propertyName, sitCount }: { hunt: Hunt; propertyName?: string; sitCount: number }) {
  const startLabel = format(parseISO(hunt.startDate), 'MMM d');
  const endLabel = format(parseISO(hunt.endDate), 'MMM d, yyyy');
  const dateRange = hunt.startDate === hunt.endDate ? startLabel + ', ' + format(parseISO(hunt.endDate), 'yyyy') : `${startLabel} – ${endLabel}`;

  return (
    <Link
      to={`/hunt/${hunt.id}`}
      className="block bg-[var(--color-surface)] border-[0.5px] border-bark-600 rounded-lg shadow-card p-4 hover:border-bark-500 transition-colors"
      data-testid={`hunt-card-${hunt.id}`}
    >
      <div className="flex items-start gap-3">
        {/* SAFETY: huntTripSvg is a static import from compiled SVG assets */}
        <span
          className="w-6 h-6 shrink-0 mt-0.5 flex items-center justify-center text-[var(--color-text-muted)]"
          aria-hidden="true"
          dangerouslySetInnerHTML={{ __html: huntTripSvg }}
        />
        <div className="flex flex-col gap-1 min-w-0 flex-1">
          <p
            className="font-display text-display-sm text-[var(--color-text-display)] truncate"
            data-testid={`hunt-name-${hunt.id}`}
            data-variant="display"
          >
            {hunt.name}
          </p>
          <p className="text-body-sm text-[var(--color-text-secondary)]">{dateRange}</p>
          {propertyName && (
            <p className="text-body-xs text-[var(--color-text-muted)]">{propertyName}</p>
          )}
        </div>

        {/* Badges */}
        <div className="flex flex-col items-end gap-1 shrink-0">
          <span
            className="inline-flex items-center px-2 py-0.5 rounded text-body-xs font-medium bg-[var(--color-elevated)] text-[var(--color-text-muted)] border border-[var(--color-border)]"
            data-testid="sit-count-badge"
          >
            {sitCount} {sitCount === 1 ? 'sit' : 'sits'}
          </span>
          {hunt.photos.length > 0 && (
            <span className="text-body-xs text-[var(--color-text-muted)]">
              {hunt.photos.length} photo{hunt.photos.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

export function HuntListPage() {
  const navigate = useNavigate();
  const { hunts, isLoading, loadHuntsForSeason, sitCountsByHuntId } = useHuntStore();
  const { seasons } = useSeasonStore();
  const isPro = useUserProfileStore((s) => s.isPro);
  const [showUpsellSheet, setShowUpsellSheet] = useState(false);

  const showUpsell = !isPro();

  // Use the most recent upcoming or active season as the primary season
  const today = new Date().toISOString().slice(0, 10);
  const primarySeason =
    seasons.find((s) => s.openerDate <= today && (!s.closerDate || s.closerDate >= today)) ??
    seasons.find((s) => s.openerDate > today) ??
    seasons[0] ??
    null;

  useEffect(() => {
    if (primarySeason) {
      void loadHuntsForSeason(primarySeason.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primarySeason?.id, loadHuntsForSeason]);

  if (showUpsell) {
    return (
      <div className="flex flex-col gap-6" data-testid="hunt-list-page">
        <h1 className="font-display text-display-lg text-[var(--color-text-display)]">
          Hunt Journal
        </h1>

        {/* Blurred preview behind upsell */}
        <div className="relative">
          <div className="opacity-30 blur-sm pointer-events-none flex flex-col gap-3" aria-hidden>
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-20 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg"
              />
            ))}
          </div>

          {/* Tap affordance to open UpsellSheet */}
          <button
            type="button"
            className="absolute inset-0 flex items-center justify-center"
            data-testid="hunt-journal-gate"
            aria-label="Unlock Hunt Journal — Pro feature"
            onClick={() => setShowUpsellSheet(true)}
          >
            <span className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl shadow-card-md mx-4 p-6 flex flex-col items-center gap-3 w-full max-w-sm">
              <span className="inline-flex items-center px-2 py-0.5 rounded text-body-xs font-semibold bg-[var(--color-accent-subtle)] text-[var(--color-accent)] border border-[var(--color-accent-pressed)]/40">
                PRO
              </span>
              <span className="font-display text-display-sm text-[var(--color-text-display)]">
                Hunt Journal
              </span>
              <span className="text-body-sm text-[var(--color-text-muted)] text-center">
                Tap to unlock with Pro
              </span>
            </span>
          </button>
        </div>

        {showUpsellSheet && (
          <UpsellSheet
            featureName={UPSELL_COPY.hunt_journal.featureName}
            description={UPSELL_COPY.hunt_journal.description}
            featureGate="hunt_journal"
            onDismiss={() => setShowUpsellSheet(false)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6" data-testid="hunt-list-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            aria-label="Go back"
            data-testid="back-button"
            className="flex items-center justify-center -ml-1 p-1 rounded text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors"
          >
            <ChevronLeft size={20} />
          </button>
          <div>
            <h1 className="font-display text-display-lg text-[var(--color-text-display)]">
              Hunt Journal
            </h1>
            {primarySeason && (
              <p className="text-body-sm text-[var(--color-text-muted)] mt-0.5">
                {primarySeason.name}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* List / Empty state */}
      {isLoading ? (
        <div className="flex flex-col gap-3 animate-pulse" aria-label="Loading hunts">
          {[1, 2].map((i) => (
            <div key={i} className="h-20 bg-[var(--color-elevated)] rounded-lg" />
          ))}
        </div>
      ) : hunts.length === 0 ? (
        <OperationalGuide
          illustration={noHuntsPng}
          heading="Start Your First Hunt"
          subtext="Log your sits and narratives to build your after-action reports."
          ctaLabel="Log a Hunt"
          onCtaClick={() => void navigate(primarySeason ? `/hunt/new?seasonId=${primarySeason.id}` : '/hunt/new')}
          data-guide-id="hunt-list-empty"
        />
      ) : (
        <div className="flex flex-col gap-3" data-testid="hunt-list">
          {hunts.map((hunt) => (
            <HuntCard
              key={hunt.id}
              hunt={hunt}
              sitCount={sitCountsByHuntId[hunt.id] ?? 0}
            />
          ))}
        </div>
      )}

      {/* FAB — only rendered when there are hunts (empty-state CTA is the primary action) */}
      {hunts.length > 0 && (
        <button
          type="button"
          onClick={() =>
            navigate(
              primarySeason
                ? `/hunt/new?seasonId=${primarySeason.id}`
                : '/hunt/new',
            )
          }
          className="fixed bottom-24 right-4 w-14 h-14 rounded-full bg-[var(--color-accent)] hover:bg-[var(--color-accent-hover)] active:bg-[var(--color-accent-pressed)] text-white shadow-ember flex items-center justify-center transition-colors"
          aria-label="Log a hunt"
          data-testid="fab-add-hunt"
        >
          <Plus size={24} />
        </button>
      )}
    </div>
  );
}
